import { Auth, Cache } from 'aws-amplify';
import axios from 'axios';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import { Component } from 'react';
import { withApollo } from 'react-apollo';
import { GetLoginUser } from 'src/_shared/api/graphql/custom/users/';
import { decryptUsingAES256 } from 'src/_shared/api/settings';
import {
	isSiteDisabledForUser,
	SITE_DISABLED_MESSAGE,
} from 'src/_shared/services/disableSite.js';
import {
	clearRedirectURL,
	consumeRedirectURL,
	peekRedirectURL,
} from 'src/_shared/services/redirectAfterLogin.js';
import { Spinner } from '../_shared/index.js';
import { Button } from 'antd';

import * as Sentry from '@sentry/react';

const lambda = async (body, auth = 'cognito') => {
	try {
		let response;

		if (auth === 'cognito') {
			const federatedToken = Cache.getItem('federatedToken');
			const Authorization = federatedToken?.id_token
				? federatedToken.id_token
				: (await Auth.currentSession()).getIdToken()?.jwtToken;
			const url =
				'https://a0youn41a1.execute-api.us-east-2.amazonaws.com/default/api';
			response = axios.post(url, body, {
				headers: {
					Authorization,
					'Access-Control-Allow-Origin': '*',
				},
			});
		} else if (auth === 'apiKey') {
			const configMode = 'PROD';
			const Authorization =
				process.env[`REACT_APP_${configMode}_LAMBDADATACALLSKEY`];
			const url =
				'https://f096mzqyo7.execute-api.us-east-2.amazonaws.com/default/api';
			response = await axios.post(url, body, {
				headers: {
					Authorization,
				},
			});
		}

		return response?.data;
	} catch (error) {
		throw error;
	}
};

const btoa = (string) => {
	try {
		return window.btoa(string);
	} catch (error) {
		console.log(error);
		return '';
	}
};
const parse = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

const auth = async (code, settings) => {
	const request = {
		code,
		requestType: 'saml',
	};
	const provider = get(settings, 'provider');
	if (provider) request.provider = provider;
	const auth = await lambda(
		{
			endpoint: 'auth-prod-api',
			variables: request,
		},
		'apiKey'
	);
	return parse(decryptUsingAES256(get(auth, 'token')));
};

const graphql = async (variables, auth = 'apiKey') => {
	try {
		const configMode = 'PROD';
		variables.configMode = configMode;
		const response = await lambda(
			{
				endpoint: `graphql`,
				variables,
			},
			auth
		);
		return response;
	} catch (error) {
		console.error(error);
	}
};

const parseJwt = (token) => {
	try {
		if (!token) {
			return;
		}

		const base64Url = token.split('.')[1]
			? token.split('.')[1]
			: token.split('=')[1];
		const base64 = base64Url
			.replaceAll('-', '+')
			.replaceAll('_', '/')
			.replaceAll('%3D', '=');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join('')
		);
		return parse(jsonPayload);
	} catch {
		return null;
	}
};
class SAMLWebLoginComponent extends Component {
	constructor(props) {
		super(props);
		this.state = {
			company: get(props, 'currentUser.company'),
			backgroundImageURL: '',
			mobileSignupComplete: false,
			inactiveAccount: false,
			siteDisabled: false,
		};
		this.onAuthentication = this.onAuthentication.bind(this);
	}

	async componentDidMount() {
		const { setCurrentUser } = this.props;
		let { host } = window.location;
		host = host.replace('www.', '');

		if (this.props.location.search.includes('error=')) {
			localStorage.removeItem('samlLoginInProgress');
			this.props.history.push({
				pathname: '/logout', // Error from idP, clear cache, try again
			});
			return;
		}

		// This is to prevent race condition by telling ApiProviderComponent.jsx to hold up a minute while we sign in
		localStorage.setItem('samlLoginInProgress', 'true');

		let federatedToken = Cache.getItem('federatedToken');
		if (!federatedToken) {
			federatedToken = await this.requestJwtToken();
		}

		if (!federatedToken && this.props.location.search.includes('code=')) {
			const hasAuthCode = this.props.location.search.includes('code=');

			if (!hasAuthCode) {
				// Redirect to company-specific SSO re-authentication
				await this.initiateSSORAuthentication();
				return;
			} else {
				localStorage.removeItem('samlLoginInProgress');
				this.props.history.push({
					pathname: '/logout',
					state: {
						err: 'Failed to authenticate with SSO provider. Please try again.',
					},
				});
				return;
			}
		}
		if (localStorage.getItem('expiresAt')) {
			const currentTime = Date.now();
			const expiresAt = localStorage.getItem('expiresAt');
			if (parseInt(expiresAt) - currentTime < 0) {
				await this.refreshToken();
				federatedToken = Cache.getItem('federatedToken');
			}
		}

		// Store the token set
		Cache.setItem('federatedToken', federatedToken);
		const idToken = parseJwt(federatedToken?.id_token);
		Cookies.set('jwt', federatedToken?.id_token);

		const expiresAt = Date.now() + federatedToken?.expires_in * 1000;
		localStorage.setItem('expiresAt', expiresAt.toString());
		if (idToken === undefined) {
			this.setState({ visible: true });
		} else {
			const issuer = idToken.iss;
			const domain = issuer.slice(8, issuer.length);
			const provider = idToken.identities[0].providerName;
			const saml = await auth(provider, { provider: true });
			const identityPoolId = saml.identityPool;
			const user = {
				name: idToken.name,
				email: idToken.email,
			};
			Auth.configure({
				identityPoolId,
				region: 'us-east-2',
			});

			let authenticatedUser;
			try {
				authenticatedUser = await Auth.currentAuthenticatedUser();

				const jwtAccessToken = federatedToken?.id_token;
				Cookies.set('jwt', jwtAccessToken);
			} catch (error) {
				// No problem, just means user not signed in yet
				await Auth.federatedSignIn(
					domain,
					{
						token: get(federatedToken, 'id_token'),
						expires_at: expiresAt,
					},
					user
				);

				authenticatedUser = await Auth.currentAuthenticatedUser();
			}

			const token = parseJwt(authenticatedUser.token);
			const email = get(token, 'email', '');
			const emailDomain = email.split('@')[1];
			const cognitoId = token?.['cognito:username'];
			const userId = token?.['custom:userId'];

			if (authenticatedUser && cognitoId) {
				// Prevent race condition
				await this.waitForAuthenticatedClient();

				let data;
				try {
					({ data } = await this.props.client.query({
						query: GetLoginUser,
						variables: {
							userId,
							token: JSON.stringify(token),
						},
						fetchPolicy: 'network-only',
					}));
				} catch (error) {
					if (
						error?.message === 'Unauthorized: Account not active' ||
						error?.code === 'UserDisabledException'
					) {
						this.handleInactiveAccount();
						return;
					}
					if (error?.message === 'Unauthorized: Site disabled') {
						this.handleSiteDisabled();
						return;
					}
					// Unexpected failure: don't strand the user on the spinner
					Sentry.captureException(error);
					localStorage.removeItem('samlLoginInProgress');
					this.props.history.push({ pathname: '/logout' });
					return;
				}
				const currentUser = data?.getLoginUser;
				if (!currentUser) {
					try {
						const provider = token?.identities?.[0]?.providerName;
						const saml = await auth(provider, { provider: true });
						let companyId = get(saml, 'companyId');

						if (provider === 'Google') {
							const company =
								await this.getCompanyIdBySSOGoogleDomain(emailDomain);
							companyId = get(company, 'id', '');
						}

						if (!companyId) {
							const error_ =
								'The provided google account does not match a company on record. Please sign in using your company email address.';
							localStorage.removeItem('samlLoginInProgress');
							this.props.history.push({
								pathname: '/login',
								state: {
									err: error_,
								},
							});
						} else if (companyId) {
							localStorage.removeItem('samlLoginInProgress');
							setCurrentUser({ companyId: companyId, cognitoId: cognitoId });
							const redirectForSignUp = consumeRedirectURL('/dashboard');
							const mobileSource = localStorage.getItem('mobileSource');
							localStorage.removeItem('mobileSource');
							this.props.history.push({
								pathname: `/newsamluser/${companyId}`,
								search: mobileSource ? '?source=mobile' : '',
								state: {
									token: token,
									companyId,
									redirectURL: redirectForSignUp,
								},
							});
						}
					} catch (e) {
						Sentry.captureException(e);
						this.props.history.push({
							pathname: '/logout',
						});
						return;
					}
				} else if (data && currentUser && !currentUser.active) {
					this.handleInactiveAccount();
					return;
				} else if (isSiteDisabledForUser(currentUser)) {
					this.handleSiteDisabled();
					return;
				} else {
					const mobileSource = localStorage.getItem('mobileSource');
					if (mobileSource) {
						localStorage.removeItem('mobileSource');
						localStorage.removeItem('samlLoginInProgress');
						this.setState({ mobileSignupComplete: true });
						return;
					}

					// Read the deep-link destination before onAuthentication: once auth
					// flips, the /saml-auth/login route itself redirects and consumes
					// the stored value, so both paths must agree. (ERIN-8413)
					const redirectURL = peekRedirectURL('/dashboard');

					setCurrentUser(currentUser);
					await this.onAuthentication(federatedToken, currentUser, token);
					let loginValue = Boolean(!currentUser.lastLoginValue) + '';
					if (currentUser.company.resetPopup) {
						loginValue = 'true';
					}

					window.localStorage.setItem('lastLoginValue', loginValue);
					window.localStorage.setItem('showFirstTimePopup', loginValue);

					clearRedirectURL();
					// Remove flag so ApiProviderComponent.jsx refreshes token as normal
					localStorage.removeItem('samlLoginInProgress');
					this.props.history.push(redirectURL);
				}
			}
		}
	}

	// Inactive/disabled account: clear the in-progress flag and show an inline
	// message instead of an endless spinner. We render this in-place rather than
	// redirecting to /login because SSO-only companies hide the login card there,
	// so a redirected message would never be displayed.
	handleInactiveAccount = () => {
		localStorage.removeItem('samlLoginInProgress');
		this.setState({ inactiveAccount: true });
	};

	// Company has disableSite set: block SSO sign-in the same way the
	// credentials form does. Rendered in place for the same reason as above.
	handleSiteDisabled = () => {
		localStorage.removeItem('samlLoginInProgress');
		this.setState({ siteDisabled: true });
	};

	waitForAuthenticatedClient = async () => {
		const federatedToken = Cache.getItem('federatedToken');
		const authenticatedUser = await Auth.currentAuthenticatedUser();
		const jwtToken = Cookies.get('jwt');
		if (
			federatedToken &&
			federatedToken.id_token &&
			authenticatedUser &&
			authenticatedUser.token &&
			jwtToken === federatedToken.id_token
		) {
			console.log('SAML authentication ready for GraphQL requests');
			return true;
		}

		throw new Error('SAML authentication not properly established');
	};

	onAuthentication = async (authToken, currentUser, token) => {
		const idToken = authToken.id_token;
		Cookies.set('jwt', idToken);
		localStorage.setItem('authType', 'saml');

		this.setState(
			{
				auth: true,
			},
			() => {}
		);
	};

	getCompanyIdBySSOGoogleDomain = async (ssoGoogleDomain) => {
		try {
			const company = await graphql({
				input: { ssoGoogleDomain },
				query: 'getCompanyBySSOGoogleDomain',
			});
			return company;
		} catch {
			return '';
		}
	};

	getMobileOperatingSystem = () => {
		const userAgent = navigator.userAgent || navigator.vendor || window.opera;
		if (/android/i.test(userAgent) && !window.MSStream) {
			const width = window.innerWidth || document.body.clientWidth;
			return width > 1000 ? 'Tablet' : 'Android';
		}

		// IOS detection from: http://stackoverflow.com/a/9039885/177710
		if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
			const width = window.innerWidth || document.body.clientWidth;
			return width > 1000 ? 'Tablet' : 'iOS';
		}
	};

	reduceJwt = (token) => {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map(function (c) {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join('')
			);
			const encoded = `${encodeURIComponent(btoa(jsonPayload))}`;
			return encoded;
		} catch (error) {
			console.log(error);
			return token;
		}
	};

	initiateSSORAuthentication = async () => {
		try {
			let federatedOrgCode = Cache.getItem('federatedOrgCode');

			if (!federatedOrgCode) {
				console.warn('No federated org code found, redirecting to login');
				this.props.history.push({
					pathname: '/logout',
				});
				return;
			}

			const federatedOrgData = await auth(federatedOrgCode);
			const domain = get(federatedOrgData, 'domain');
			const clientId = get(federatedOrgData, 'clientId');
			const region = get(federatedOrgData, 'region', 'us-east-2');

			if (!domain || !clientId) {
				throw new Error('Invalid federated org configuration');
			}

			Cache.setItem('federatedDomain', domain);
			Cache.setItem('federatedRegion', region);
			Cache.setItem('federatedClientId', clientId);

			const { origin } = window.location;
			const redirectUri = `${origin}/saml-auth/login`;
			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const responseType = 'code';

			const ssoUrl = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${encodeURIComponent(redirectUri)}`;

			console.log('Redirecting to SSO re-authentication');
			window.location.href = ssoUrl;
		} catch (error) {
			Sentry.captureException(error);
			console.error('Error initiating SSO re-authentication:', error);
			this.props.history.push({
				pathname: '/logout',
			});
		}
	};

	refreshToken = async () => {
		try {
			const federatedToken = Cache.getItem('federatedToken');
			if (!federatedToken || !federatedToken.refresh_token) {
				console.log(
					'No federated token or refresh token found, redirecting to logout'
				);
				window.location.href = `${window.location.origin}/logout`;
			}

			const refreshToken = federatedToken.refresh_token;
			const domain = Cache.getItem('federatedDomain');
			const region = Cache.getItem('federatedRegion');
			const clientId = Cache.getItem('federatedClientId');
			const clientSecret = Cache.getItem('federatedClientSecret');

			if (!domain || !region || !clientId || !clientSecret) {
				console.error('Missing required federation parameters');
				throw new Error('Missing federation parameters');
			}

			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const body = `client_id=${clientId}&client_secret=${clientSecret}&grant_type=refresh_token&refresh_token=${refreshToken}&scope=${scope}`;
			const url = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`;

			const response = await axios.post(url, body, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					Authorization: 'Basic ' + window.btoa(clientId + ':' + clientSecret),
				},
			});

			const tokens = response.data;

			if (!tokens.refresh_token && federatedToken.refresh_token) {
				tokens.refresh_token = federatedToken.refresh_token;
			}

			Cache.setItem('federatedToken', tokens);

			const token = tokens.id_token;
			Cookies.set('jwt', token);

			const expiresAt = Date.now() + tokens.expires_in * 1000;
			localStorage.setItem('expiresAt', expiresAt.toString());

			return token;
		} catch (error) {
			Sentry.captureException(error);
			console.error('Error refreshing federated token:', error);
			window.location.href = `${window.location.origin}/logout`;
		}
	};

	async requestJwtToken() {
		try {
			// Only the code: a trailing parameter (e.g. state) must not be sent to
			// Cognito as part of it.
			const code = new URLSearchParams(
				get(this.props, 'location.search', '')
			).get('code');
			const federatedOrgCode = Cache.getItem('federatedOrgCode');
			if (!federatedOrgCode) {
				console.warn('No federated org code found, redirecting to login');
				this.props.history.push({
					pathname: '/logout',
				});
				return;
			}
			const federatedOrgData = await auth(federatedOrgCode);
			const clientId = get(federatedOrgData, 'clientId');
			const clientSecret = get(federatedOrgData, 'clientSecret');
			const domain = get(federatedOrgData, 'domain');
			const region = get(federatedOrgData, 'region', 'us-east-2');
			Cache.setItem('federatedDomain', domain);
			Cache.setItem('federatedRegion', region);
			Cache.setItem('federatedClientId', clientId);
			Cache.setItem('federatedClientSecret', clientSecret);
			const { origin } = window.location;
			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const body = `client_id=${clientId}&client_secret=${clientSecret}&grant_type=authorization_code&code=${code}&scope=${scope}&redirect_uri=${origin}/saml-auth/login`;
			const url = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`;
			const response = await axios.post(url, body, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					Authorization: 'Basic ' + btoa(clientId + ':' + clientSecret),
				},
			});
			const federatedToken = response.data;
			const expiresAt = Date.now() + federatedToken.expires_in * 1000;
			localStorage.setItem('expiresAt', expiresAt.toString());
			Cache.setItem('federatedToken', federatedToken);
			return federatedToken;
		} catch (error) {
			Sentry.captureException(error);
			console.log(error);
			return undefined;
		}
	}

	// Inactive account and disabled site both end the SSO flow with an in-place
	// message and a way back to the login page.
	renderBlockedScreen = (title, message) => (
		<div style={{ textAlign: 'center', padding: '40px 20px' }}>
			<h2 style={{ textAlign: 'center' }}>{title}</h2>
			<p
				style={{
					fontSize: 16,
					color: '#555',
					marginBottom: 24,
					lineHeight: '1.5',
				}}
			>
				{message}
			</p>
			<Button
				type="primary"
				size="large"
				onClick={() => this.props.history.push({ pathname: '/logout' })}
			>
				Return to Login
			</Button>
		</div>
	);

	render() {
		if (this.state.inactiveAccount) {
			return this.renderBlockedScreen(
				'Account Disabled',
				'Your account has been disabled. Please contact an administrator to reactivate your account.'
			);
		}

		if (this.state.siteDisabled) {
			return this.renderBlockedScreen('License Invalid', SITE_DISABLED_MESSAGE);
		}

		if (this.state.mobileSignupComplete) {
			return (
				<div style={{ textAlign: 'center', padding: '40px 20px' }}>
					<div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
					<h2 style={{ textAlign: 'center' }}>You're All Set!</h2>
					<p
						style={{
							fontSize: 16,
							color: '#555',
							marginBottom: 24,
							lineHeight: '1.5',
						}}
					>
						Close this browser and log in on the mobile app.
					</p>
				</div>
			);
		}

		return <Spinner forceDefault />;
	}
}

export default withApollo(SAMLWebLoginComponent);
